/**
 * Chunk load/unload around a focus point + solid queries for physics.
 * Generation and remesh are both budgeted per frame (count + wall-clock ms)
 * so the player cannot stall the main thread while sprinting. Outer ring
 * fills over frames; near ring stays solid for collision.
 *
 * Playtest 2.8: optional velocity lookahead bias so the heading disk is
 * force-queued / priority-sorted ahead of the feet — cuts walk-to-empty edge.
 */

import * as THREE from "three";
import { isIndestructible, isSolid } from "./blocks";
import {
  CHUNK_HEIGHT,
  CHUNK_SIZE,
  Chunk,
  type WorldgenOptions,
  chunkKey,
  generateChunk,
  worldToChunk,
} from "./chunk";
import { getChunkMaterial } from "./material";
import { meshChunk, type GrassDetail, type MeshData } from "./mesher";

export interface WorldConfig {
  viewDistance: number; // chunks radius
  worldgen: WorldgenOptions;
  /** Max dirty chunks to remesh per updateAround call (FPS). */
  remeshBudget?: number;
  /** Max new chunks to generate per updateAround call. */
  genBudget?: number;
  /**
   * Chunks within this radius of the player are generated synchronously
   * when missing (collision + feet must exist). Outer ring is queued.
   */
  forceGenRadius?: number;
  /**
   * Soft wall-clock budget (ms) for gen+remesh work inside one updateAround.
   * Prevents multi-chunk gen spikes from tanking a frame on weaker GPUs.
   */
  streamTimeBudgetMs?: number;
  /**
   * Extra chunk cells to force-queue along movement heading (playtest 2.8).
   * Combined with velocity so sprint preloads the edge you walk into.
   */
  lookAheadChunks?: number;
  /**
   * Phase 2.5: chunk radius for full grass detail (inside = full blades).
   * Beyond this → sparse; beyond farGrassRadius → no grass.
   */
  nearDetailRadius?: number;
  /** Phase 2.5: beyond this radius, skip tall-grass mesh entirely. */
  farGrassRadius?: number;
}

type Pending = {
  cx: number;
  cz: number;
  d2: number;
  /** Sort key — feet distance with heading bias (lower = sooner). */
  prio: number;
  force?: boolean;
};

export class World {
  readonly group = new THREE.Group();
  private readonly chunks = new Map<string, Chunk>();
  private readonly meshes = new Map<string, THREE.Mesh>();
  private readonly cfg: WorldConfig;
  private lastFocusCx = Number.NaN;
  private lastFocusCz = Number.NaN;
  private lastLookCx = 0;
  private lastLookCz = 0;
  private readonly remeshBudget: number;
  private readonly genBudget: number;
  private readonly forceGenRadius: number;
  private readonly streamTimeBudgetMs: number;
  private readonly lookAheadChunks: number;
  private nearDetailRadius: number;
  private farGrassRadius: number;
  /** Feet chunk used last frame — LOD mesh detail is distance-based. */
  private lodFocusCx = 0;
  private lodFocusCz = 0;
  /** Outer-ring chunks waiting for generateChunk (sorted each drain). */
  private readonly pendingGen = new Map<string, Pending>();
  /** Soft reuse of sorted pending list when focus stable. */
  private pendingSorted: Pending[] = [];
  private pendingSortedDirty = true;
  /** Detail tier last applied per mesh key — remesh when LOD band changes. */
  private readonly meshDetailTier = new Map<string, GrassDetail>();

  constructor(cfg: WorldConfig) {
    this.cfg = cfg;
    this.remeshBudget = Math.max(1, cfg.remeshBudget ?? 6);
    this.genBudget = Math.max(1, cfg.genBudget ?? 6);
    this.forceGenRadius = Math.max(1, cfg.forceGenRadius ?? 3);
    // 0 is valid (tests / emergency starve); default 6ms soft frame budget
    this.streamTimeBudgetMs =
      cfg.streamTimeBudgetMs !== undefined
        ? Math.max(0, cfg.streamTimeBudgetMs)
        : 6;
    this.lookAheadChunks = Math.max(0, cfg.lookAheadChunks ?? 4);
    this.nearDetailRadius = Math.max(1, cfg.nearDetailRadius ?? 8);
    this.farGrassRadius = Math.max(
      this.nearDetailRadius + 1,
      cfg.farGrassRadius ?? 18,
    );
    this.group.name = "world";
  }

  get seed(): number {
    return this.cfg.worldgen.seed;
  }

  get viewDistance(): number {
    return this.cfg.viewDistance;
  }

  /**
   * Phase 2.5: live view-radius change (adaptive shrink under load).
   * Marks outer meshes dirty when radius grows so LOD tiers refresh.
   */
  setViewDistance(r: number): void {
    const next = Math.max(2, Math.min(64, Math.round(r)));
    if (next === this.cfg.viewDistance) return;
    const prev = this.cfg.viewDistance;
    this.cfg.viewDistance = next;
    this.lastFocusCx = Number.NaN; // force ring rebuild next updateAround
    // Growing view: far chunks may need grass stripped / re-tiered on first mesh
    if (next < prev) {
      // Shrinking: unload happens on next rebuildNeededRing
    }
  }

  /**
   * Swap worldgen options and wipe loaded chunks (seed change / regenerate).
   * Caller should force-stream around spawn afterward.
   */
  setWorldgen(opts: WorldgenOptions): void {
    this.cfg.worldgen = opts;
    this.lastFocusCx = Number.NaN;
    this.lastFocusCz = Number.NaN;
    this.lodFocusCx = Number.NaN;
    this.lodFocusCz = Number.NaN;
    this.pendingGen.clear();
    this.pendingSorted = [];
    this.pendingSortedDirty = true;
    this.meshDetailTier.clear();
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh);
      mesh.geometry.dispose();
    }
    this.meshes.clear();
    this.chunks.clear();
  }

  getChunk(cx: number, cz: number): Chunk | undefined {
    return this.chunks.get(chunkKey(cx, cz));
  }

  /** Block id at world integer coords. */
  getBlock(wx: number, wy: number, wz: number): number {
    if (wy < 0 || wy >= CHUNK_HEIGHT) return 0;
    const { cx, cz } = worldToChunk(wx, wz);
    const ch = this.chunks.get(chunkKey(cx, cz));
    if (!ch) return 0;
    const lx = wx - cx * CHUNK_SIZE;
    const lz = wz - cz * CHUNK_SIZE;
    return ch.get(lx, wy, lz);
  }

  isSolidWorld(wx: number, wy: number, wz: number): boolean {
    return isSolid(this.getBlock(Math.floor(wx), Math.floor(wy), Math.floor(wz)));
  }

  /**
   * Gameplay block write. Rejects out-of-bounds, unloaded chunks, and
   * indestructible cells (bedrock floor). Marks chunk + neighbors dirty.
   * @returns true if the cell changed.
   */
  setBlock(wx: number, wy: number, wz: number, id: number): boolean {
    const x = Math.floor(wx);
    const y = Math.floor(wy);
    const z = Math.floor(wz);
    // y=0 is the indestructible bedrock floor (full rescale band law).
    if (y <= 0 || y >= CHUNK_HEIGHT) return false;
    if (isIndestructible(id)) return false; // never place bedrock via gameplay
    const { cx, cz } = worldToChunk(x, z);
    const ch = this.chunks.get(chunkKey(cx, cz));
    if (!ch) return false;
    const lx = x - cx * CHUNK_SIZE;
    const lz = z - cz * CHUNK_SIZE;
    const cur = ch.get(lx, y, lz);
    if (isIndestructible(cur)) return false;
    if (cur === id) return false;
    ch.set(lx, y, lz, id);
    ch.dirty = true;
    this.markNeighborMeshesDirty(cx, cz);
    return true;
  }

  /**
   * Cold-start: sync-generate every chunk in Chebyshev radius r of world xz
   * (and remesh dirty). Used by the loading overlay before first frame.
   */
  preloadRing(wx: number, wz: number, r: number): void {
    const { cx: fcx, cz: fcz } = worldToChunk(wx, wz);
    const rad = Math.max(0, Math.floor(r));
    for (let dz = -rad; dz <= rad; dz++) {
      for (let dx = -rad; dx <= rad; dx++) {
        const cx = fcx + dx;
        const cz = fcz + dz;
        const key = chunkKey(cx, cz);
        if (!this.chunks.has(key)) this.generateOne(cx, cz);
      }
    }
    // Drain all dirty meshes in the ring (no per-frame budget on cold start).
    for (const ch of this.chunks.values()) {
      if (!ch.dirty && this.meshes.has(chunkKey(ch.cx, ch.cz))) continue;
      const d = Math.max(Math.abs(ch.cx - fcx), Math.abs(ch.cz - fcz));
      if (d > rad) continue;
      const key = chunkKey(ch.cx, ch.cz);
      const data = this.buildMesh(ch);
      this.applyMesh(key, ch, data);
      ch.dirty = false;
      ch.meshVersion++;
    }
  }

  /**
   * True when every chunk in Chebyshev radius r around world xz is loaded
   * and has a mesh (not dirty / pending).
   */
  isRingReady(wx: number, wz: number, r: number): boolean {
    const { cx: fcx, cz: fcz } = worldToChunk(wx, wz);
    const rad = Math.max(0, Math.floor(r));
    for (let dz = -rad; dz <= rad; dz++) {
      for (let dx = -rad; dx <= rad; dx++) {
        const cx = fcx + dx;
        const cz = fcz + dz;
        const key = chunkKey(cx, cz);
        const ch = this.chunks.get(key);
        if (!ch || ch.dirty) return false;
        if (!this.meshes.has(key)) return false;
      }
    }
    return true;
  }

  /**
   * Ensure chunks in radius around world xz are loaded; unload far ones.
   * Call each frame. Near ring is prioritized; gen+remesh share a wall-clock
   * budget so sprinting does not stall the main thread (Playtest 2.6).
   *
   * @param velX / velZ optional world-units/sec velocity for heading preload
   *   (playtest 2.8 — walk-into-empty edge).
   */
  updateAround(wx: number, wz: number, velX = 0, velZ = 0): void {
    const { cx: fcx, cz: fcz } = worldToChunk(wx, wz);
    const { lookCx, lookCz } = this.computeLookChunk(fcx, fcz, velX, velZ);
    const t0 = performance.now();
    const deadline = t0 + this.streamTimeBudgetMs;

    const focusChanged =
      fcx !== this.lastFocusCx ||
      fcz !== this.lastFocusCz ||
      lookCx !== this.lastLookCx ||
      lookCz !== this.lastLookCz;
    if (focusChanged) {
      this.lastFocusCx = fcx;
      this.lastFocusCz = fcz;
      this.lastLookCx = lookCx;
      this.lastLookCz = lookCz;
      this.rebuildNeededRing(fcx, fcz, lookCx, lookCz);
      this.pendingSortedDirty = true;
    }

    // Phase 2.5: when feet chunk moves, re-tier grass LOD on crossed bands
    if (fcx !== this.lodFocusCx || fcz !== this.lodFocusCz) {
      this.lodFocusCx = fcx;
      this.lodFocusCz = fcz;
      this.markLodTierDirties(fcx, fcz);
    }

    // Force ring first (time-sliced), then outer pending, then remesh.
    this.drainForceRing(fcx, fcz, lookCx, lookCz, deadline);
    this.drainGenBudget(this.genBudget, fcx, fcz, lookCx, lookCz, deadline);
    this.remeshDirty(this.remeshBudget, fcx, fcz, lookCx, lookCz, deadline);
  }

  /** Grass detail tier from chebyshev-ish chunk distance to feet. */
  private grassDetailAt(cx: number, cz: number, fcx: number, fcz: number): GrassDetail {
    const d = Math.sqrt((cx - fcx) ** 2 + (cz - fcz) ** 2);
    if (d <= this.nearDetailRadius) return "full";
    if (d <= this.farGrassRadius) return "sparse";
    return "none";
  }

  /**
   * When the player crosses a chunk boundary, dirty meshes whose detail tier
   * would change (near/sparse/none bands). Cheap — only checks loaded meshes.
   */
  private markLodTierDirties(fcx: number, fcz: number): void {
    for (const [key, chunk] of this.chunks) {
      if (chunk.dirty) continue;
      const want = this.grassDetailAt(chunk.cx, chunk.cz, fcx, fcz);
      const had = this.meshDetailTier.get(key);
      if (had !== want) {
        chunk.dirty = true;
      }
    }
  }

  /**
   * Project feet + velocity into a look-focus chunk a few cells ahead.
   * Stationary → look focus = feet (no bias).
   */
  private computeLookChunk(
    fcx: number,
    fcz: number,
    velX: number,
    velZ: number,
  ): { lookCx: number; lookCz: number } {
    const la = this.lookAheadChunks;
    if (la <= 0) return { lookCx: fcx, lookCz: fcz };
    const speed = Math.hypot(velX, velZ);
    // ~half walk speed in voxel units (~50) before bias engages
    if (speed < 40) return { lookCx: fcx, lookCz: fcz };
    // Cap bias cells by speed: walk ~2, sprint ~full lookAhead
    const cells = Math.min(la, Math.max(1, Math.round((speed / 160) * la)));
    const inv = 1 / speed;
    const dx = Math.round(velX * inv * cells);
    const dz = Math.round(velZ * inv * cells);
    return { lookCx: fcx + dx, lookCz: fcz + dz };
  }

  private priorityAt(
    cx: number,
    cz: number,
    fcx: number,
    fcz: number,
    lookCx: number,
    lookCz: number,
  ): number {
    const dFeet = (cx - fcx) ** 2 + (cz - fcz) ** 2;
    const dLook = (cx - lookCx) ** 2 + (cz - lookCz) ** 2;
    // Blend: heading disk slightly preferred so the edge you walk into fills first
    return dFeet * 0.55 + dLook * 0.45;
  }

  private rebuildNeededRing(
    fcx: number,
    fcz: number,
    lookCx: number,
    lookCz: number,
  ): void {
    const r = this.cfg.viewDistance;
    const needed = new Set<string>();
    const r2 = (r + 0.5) * (r + 0.5);
    const forceR2 = (this.forceGenRadius + 0.5) * (this.forceGenRadius + 0.5);
    // Small disk around look focus also force-queued (heading preload)
    const lookForceR = Math.min(2, this.forceGenRadius);
    const lookForceR2 = (lookForceR + 0.5) * (lookForceR + 0.5);

    // Playtest 2.8: feet disk ∪ look disk so heading chunks exist beyond the
    // underfoot ring (walk-into-empty / pop-in). When stationary look==feet.
    const enqueueDisk = (ocx: number, ocz: number): void => {
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          if (dx * dx + dz * dz > r2) continue;
          const cx = ocx + dx;
          const cz = ocz + dz;
          const key = chunkKey(cx, cz);
          if (needed.has(key)) continue;
          needed.add(key);
          if (this.chunks.has(key)) {
            this.pendingGen.delete(key);
            continue;
          }
          const d2 = (cx - fcx) ** 2 + (cz - fcz) ** 2;
          const dLook = (cx - lookCx) ** 2 + (cz - lookCz) ** 2;
          const force = d2 <= forceR2 || dLook <= lookForceR2;
          this.pendingGen.set(key, {
            cx,
            cz,
            d2,
            prio: this.priorityAt(cx, cz, fcx, fcz, lookCx, lookCz),
            force,
          });
        }
      }
    };
    enqueueDisk(fcx, fcz);
    if (lookCx !== fcx || lookCz !== fcz) enqueueDisk(lookCx, lookCz);

    // Drop pending that left the ring
    for (const key of [...this.pendingGen.keys()]) {
      if (!needed.has(key)) this.pendingGen.delete(key);
    }

    // Unload outside feet∪look + hysteresis (don't thrash heading preloads)
    const unloadR = r + 2;
    const unloadR2 = (unloadR + 0.5) * (unloadR + 0.5);
    for (const key of [...this.chunks.keys()]) {
      if (needed.has(key)) continue;
      const [sx, sz] = key.split(",").map(Number) as [number, number];
      const dFeet = (sx - fcx) ** 2 + (sz - fcz) ** 2;
      const dLook = (sx - lookCx) ** 2 + (sz - lookCz) ** 2;
      if (dFeet <= unloadR2 || dLook <= unloadR2) continue;
      this.chunks.delete(key);
      this.pendingGen.delete(key);
      this.meshDetailTier.delete(key);
      const mesh = this.meshes.get(key);
      if (mesh) {
        this.group.remove(mesh);
        mesh.geometry.dispose();
        this.meshes.delete(key);
      }
    }
  }

  /**
   * Generate missing force-ring chunks priority-first until wall-clock deadline.
   * Always does at least one force chunk if any are pending (collision priority).
   */
  private drainForceRing(
    fcx: number,
    fcz: number,
    lookCx: number,
    lookCz: number,
    deadline: number,
  ): void {
    const forceR2 = (this.forceGenRadius + 0.5) * (this.forceGenRadius + 0.5);
    const lookForceR = Math.min(2, this.forceGenRadius);
    const lookForceR2 = (lookForceR + 0.5) * (lookForceR + 0.5);
    const force: Pending[] = [];
    for (const p of this.pendingGen.values()) {
      const d2 = (p.cx - fcx) ** 2 + (p.cz - fcz) ** 2;
      const dLook = (p.cx - lookCx) ** 2 + (p.cz - lookCz) ** 2;
      p.d2 = d2;
      p.prio = this.priorityAt(p.cx, p.cz, fcx, fcz, lookCx, lookCz);
      if (d2 <= forceR2 || dLook <= lookForceR2 || p.force) {
        p.force = true;
        force.push(p);
      }
    }
    if (force.length === 0) return;
    force.sort((a, b) => a.prio - b.prio || a.d2 - b.d2);

    let n = 0;
    for (const p of force) {
      if (n > 0 && performance.now() >= deadline) break;
      const key = chunkKey(p.cx, p.cz);
      if (this.chunks.has(key)) {
        this.pendingGen.delete(key);
        continue;
      }
      this.generateOne(p.cx, p.cz);
      this.pendingGen.delete(key);
      n++;
    }
    if (n > 0) this.pendingSortedDirty = true;
  }

  private generateOne(cx: number, cz: number): Chunk {
    const key = chunkKey(cx, cz);
    let ch = this.chunks.get(key);
    if (ch) return ch;
    ch = generateChunk(cx, cz, this.cfg.worldgen);
    this.chunks.set(key, ch);
    // Neighbor faces may have been meshed against air; mark for remesh once
    // the new column exists (cheap dirties — budgeted remesh drains them).
    this.markNeighborMeshesDirty(cx, cz);
    return ch;
  }

  /** When a chunk appears, adjacent loaded meshes need seam refresh. */
  private markNeighborMeshesDirty(cx: number, cz: number): void {
    const neigh = [
      [cx - 1, cz],
      [cx + 1, cz],
      [cx, cz - 1],
      [cx, cz + 1],
    ] as const;
    for (const [nx, nz] of neigh) {
      const n = this.chunks.get(chunkKey(nx, nz));
      if (n) n.dirty = true;
    }
  }

  private ensurePendingSorted(
    fcx: number,
    fcz: number,
    lookCx: number,
    lookCz: number,
  ): Pending[] {
    if (!this.pendingSortedDirty && this.pendingSorted.length === this.pendingGen.size) {
      // Refresh distances to current focus without full rebuild when possible
      for (const p of this.pendingSorted) {
        p.d2 = (p.cx - fcx) ** 2 + (p.cz - fcz) ** 2;
        p.prio = this.priorityAt(p.cx, p.cz, fcx, fcz, lookCx, lookCz);
      }
      this.pendingSorted.sort((a, b) => a.prio - b.prio || a.d2 - b.d2);
      return this.pendingSorted;
    }
    const list = [...this.pendingGen.values()];
    for (const p of list) {
      p.d2 = (p.cx - fcx) ** 2 + (p.cz - fcz) ** 2;
      p.prio = this.priorityAt(p.cx, p.cz, fcx, fcz, lookCx, lookCz);
    }
    list.sort((a, b) => a.prio - b.prio || a.d2 - b.d2);
    this.pendingSorted = list;
    this.pendingSortedDirty = false;
    return list;
  }

  private drainGenBudget(
    budget: number,
    fcx: number,
    fcz: number,
    lookCx: number,
    lookCz: number,
    deadline: number,
  ): void {
    if (this.pendingGen.size === 0 || budget <= 0) return;

    const list = this.ensurePendingSorted(fcx, fcz, lookCx, lookCz);
    const r = this.cfg.viewDistance;
    const r2 = (r + 0.5) * (r + 0.5);

    let n = 0;
    let i = 0;
    while (i < list.length && n < budget) {
      if (n > 0 && performance.now() >= deadline) break;
      const p = list[i]!;
      i++;
      const key = chunkKey(p.cx, p.cz);
      if (this.chunks.has(key)) {
        this.pendingGen.delete(key);
        continue;
      }
      // Playtest 2.9: keep feet∪look disk — do not drop heading preload
      // chunks just because they sit outside the underfoot radius alone.
      const dFeet = (p.cx - fcx) ** 2 + (p.cz - fcz) ** 2;
      const dLook = (p.cx - lookCx) ** 2 + (p.cz - lookCz) ** 2;
      if (dFeet > r2 && dLook > r2) {
        this.pendingGen.delete(key);
        continue;
      }
      this.generateOne(p.cx, p.cz);
      this.pendingGen.delete(key);
      n++;
    }
    if (n > 0) this.pendingSortedDirty = true;
  }

  private remeshDirty(
    budget: number,
    fcx: number,
    fcz: number,
    lookCx: number,
    lookCz: number,
    deadline: number,
  ): void {
    const dirty: Chunk[] = [];
    for (const chunk of this.chunks.values()) {
      if (!chunk.dirty && this.meshes.has(chunkKey(chunk.cx, chunk.cz))) continue;
      dirty.push(chunk);
    }
    if (dirty.length === 0) return;

    dirty.sort((a, b) => {
      const pa = this.priorityAt(a.cx, a.cz, fcx, fcz, lookCx, lookCz);
      const pb = this.priorityAt(b.cx, b.cz, fcx, fcz, lookCx, lookCz);
      return pa - pb;
    });

    // Adaptive: if a big backlog near the player, spend a bit more this frame.
    let spend = budget;
    if (dirty.length > budget * 3) spend = budget + 2;
    if (dirty.length > 0) {
      const nearest = dirty[0]!;
      const nd = (nearest.cx - fcx) ** 2 + (nearest.cz - fcz) ** 2;
      if (nd <= 4) spend = Math.max(spend, budget + 1);
    }

    const nMax = Math.min(spend, dirty.length);
    let n = 0;
    for (let i = 0; i < nMax; i++) {
      if (n > 0 && performance.now() >= deadline) break;
      const chunk = dirty[i]!;
      const key = chunkKey(chunk.cx, chunk.cz);
      const data = this.buildMesh(chunk);
      this.applyMesh(key, chunk, data);
      chunk.dirty = false;
      chunk.meshVersion++;
      n++;
    }
  }

  private buildMesh(chunk: Chunk): MeshData {
    const ox = chunk.cx * CHUNK_SIZE;
    const oz = chunk.cz * CHUNK_SIZE;
    const getNeighbor = (lx: number, ly: number, lz: number): number => {
      if (ly < 0 || ly >= CHUNK_HEIGHT) return 0;
      const wx = ox + lx;
      const wz = oz + lz;
      return this.getBlock(wx, ly, wz);
    };
    const grassDetail = this.grassDetailAt(
      chunk.cx,
      chunk.cz,
      this.lodFocusCx,
      this.lodFocusCz,
    );
    const key = chunkKey(chunk.cx, chunk.cz);
    this.meshDetailTier.set(key, grassDetail);
    // Y-band is auto inside mesher (solidYBounds ± pad). Grass LOD by distance.
    return meshChunk(chunk, getNeighbor, { grassDetail });
  }

  private applyMesh(key: string, chunk: Chunk, data: MeshData): void {
    let mesh = this.meshes.get(key);
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(data.positions, 3));
    geom.setAttribute("normal", new THREE.BufferAttribute(data.normals, 3));
    geom.setAttribute("color", new THREE.BufferAttribute(data.colors, 3));
    geom.computeBoundingSphere();

    if (!mesh) {
      mesh = new THREE.Mesh(geom, getChunkMaterial());
      mesh.name = `chunk_${key}`;
      mesh.position.set(chunk.cx * CHUNK_SIZE, 0, chunk.cz * CHUNK_SIZE);
      mesh.frustumCulled = true;
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      this.meshes.set(key, mesh);
      this.group.add(mesh);
    } else {
      mesh.geometry.dispose();
      mesh.geometry = geom;
      mesh.position.set(chunk.cx * CHUNK_SIZE, 0, chunk.cz * CHUNK_SIZE);
      mesh.updateMatrix();
    }
  }

  /** Surface Y (top solid) near xz, or SEA-ish default. */
  findSurfaceY(wx: number, wz: number): number {
    for (let y = CHUNK_HEIGHT - 1; y >= 0; y--) {
      if (this.isSolidWorld(wx, y, wz)) return y + 1;
    }
    return 14;
  }

  get loadedChunkCount(): number {
    return this.chunks.size;
  }

  get meshCount(): number {
    return this.meshes.size;
  }

  get pendingGenCount(): number {
    return this.pendingGen.size;
  }

  dispose(): void {
    for (const mesh of this.meshes.values()) {
      this.group.remove(mesh);
      mesh.geometry.dispose();
    }
    this.meshes.clear();
    this.chunks.clear();
    this.pendingGen.clear();
    this.pendingSorted = [];
    this.pendingSortedDirty = true;
    this.meshDetailTier.clear();
  }
}
